import React, {useState, useEffect} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import DatePicker from "react-datepicker";
import {
    calculateDaysDifference,
    formatDateToAmericanFormat,
    formatDateToString, formatLocalDate, parseLocalDate, removeTimeFromDate
} from "../../utils/utils";
import {getPaymentById} from "../../utils/requestUtils";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import {getAuthHeader} from "../../utils/auth";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import {saveAs} from 'file-saver';
import NewButton from '../common/NewButton';
import SearchButton from '../common/SearchButton';
import TableRowActions from '../common/TableRowActions';
import ListPageLayout from '../common/ListPageLayout';
import { showConfirm } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const PaymentList = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const [payments, setPayments] = useState([]);
    const [startDate, setStartDate] = useState(undefined);
    const initialEndDate = undefined;
    const [endDate, setEndDate] = useState(initialEndDate);
    const [status, setStatus] = useState(0);
    const [totalValue, setTotalValue] = useState(0);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();

    async function makeQuery() {
        try {
            setLoading(true);
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);

            const url = `${apiUrl}/api/payments/queryByPaymentDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}`;
            const response = await makeRequest(url, 'GET');
            setPayments(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery();
    }, []);

    useEffect(() => {
        setTotalValue(payments.reduce((acc, obj) => acc + obj.value, 0));
    }, [payments]);

    async function deletePayment(id) {
        try {
            setLoading(true);
            const url = apiUrl + '/api/payments/' + id;
            await makeRequest(url, 'DELETE');
            await makeQuery();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleQuery = async (e) => {
        await makeQuery();
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const generateExcelFile = async () => {
        try {
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/payments/excelByPaymentDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}`;

            const response = await fetch(url, {headers: {...getAuthHeader()}});
            if (response.ok) {
                const data = await response.blob();
                saveAs(data, `relatorio-pagamentos-${formatDateToAmericanFormat(newStartDate)}-${formatDateToAmericanFormat(newEndDate)}.xlsx`);
            } else {
                console.error('Erro ao gerar o arquivo Excel:', response.statusText);
            }
        } catch (error) {
            console.error('Erro ao fazer a solicitação para gerar o arquivo Excel:', error);
        }
    };

    const onPayClick = async (id) => {
        const payment = await getPaymentById(id);
        if (!await showConfirm(`Deseja realmente ${!payment.paymentDate ? "Baixar" : "Estornar"}?`)) {
            return;
        }
        if (payment.paymentDate) {
            payment.paymentDate = undefined;
            payment.status = 0;
        } else {
            payment.paymentDate = formatLocalDate(new Date());
            payment.status = 1;
        }

        const url = apiUrl + '/api/payments';
        await makeRequest(url, 'PUT', {
            'Content-Type': 'application/json',
        }, JSON.stringify(payment));

        await makeQuery();
    };

    function getBackgroundColor(payment) {
        const currentDate = removeTimeFromDate(new Date());
        const dueDate = parseLocalDate(payment.dueDate);
        const errorColor = 'var(--error-color)';
        const warningColor = 'var(--warning-color)';
        if (payment.status === 0) {
            if (dueDate < currentDate)
                return errorColor;
            if (calculateDaysDifference(dueDate, currentDate) === 0) {
                return warningColor;
            }
        }
        return undefined;
    }

    const buildPaymentRowActions = (payment) => {
        const actions = [
            {
                key: 'pay',
                label: payment.paymentDate ? 'Estornar' : 'Baixar',
                type: payment.paymentDate ? 'reverse' : 'confirm',
                onClick: () => onPayClick(payment.id),
                buttonProps: payment.paymentDate ? {} : {className: 'finish-button'},
            },
        ];

        if (hasUserPermission(UserPermission.EDIT_PAYMENT)) {
            actions.push({
                key: 'edit',
                label: 'Editar',
                type: 'edit',
                onClick: () => navigate('/app/payment?id=' + payment.id),
            });
        }

        if (hasUserPermission(UserPermission.REMOVE_PAYMENT)) {
            actions.push({
                key: 'delete',
                label: 'Excluir',
                type: 'delete',
                onClick: async () => {
                    if (await showConfirm('Deseja realmente excluir?')) {
                        await deletePayment(payment.id);
                    }
                },
            });
        }

        return actions;
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (hasUserPermission(UserPermission.LIST_PAYMENT) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <ListPageLayout
            tableWidth={1000}
            header={
                <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                    <div className="header-label">Consulta de Pagamentos</div>
                    {hasUserPermission(UserPermission.CREATE_PAYMENT) &&
                        <NewButton className={"new-button"} onClick={() => navigate('/app/payment')}/>
                    }
                </div>
            }
            filters={
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>

                    Data de Vencimento:
                    <div className="list-page-filters-period" style={{display: 'flex', alignItems: 'center'}}>
                        <DatePicker
                            className="datepicker"
                            selected={startDate}
                            onChange={handleStartDateChange}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />

                        <span className="list-page-filters-period-separator">até</span>
                        <DatePicker
                            className="datepicker"
                            selected={endDate}
                            onChange={handleEndDateChange}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />

                        <select value={status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                            <option key={-1} value={-1}>Todos</option>
                            <option key={0} value={0}>Pendente</option>
                            <option key={1} value={1}>Pago</option>
                        </select>

                        <SearchButton title="Consultar" onClick={handleQuery}/>
                    </div>
                </div>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <button className="excel-button" onClick={generateExcelFile}>Excel</button>
                </div>
                </div>
            }
            footer={
                <div className="total-label">Total: R$ {totalValue?.toFixed(2)}</div>
            }
        >
            <div className="list-page-payments">
            <table>
                <thead>
                <tr>
                    <th className="payment-col--optional">Código</th>
                    <th className="payment-col--essential">Cliente</th>
                    <th className="payment-col--optional">Descrição</th>
                    <th className="payment-col--optional">Lançamento</th>
                    <th className="payment-col--essential">Vencimento</th>
                    <th className="payment-col--optional">Pagamento</th>
                    <th className="payment-col--essential">Valor</th>
                    <th className="payment-col--actions"/>
                </tr>
                </thead>
                <tbody style={{overflowY: 'auto'}}>
                {payments.map((payment) => (
                    <tr key={payment.id} style={{backgroundColor: getBackgroundColor(payment)}}>
                        <td className="payment-col--optional">
                            <div>{payment.code}</div>
                        </td>
                        <td className="payment-col--essential">
                            <div>{payment.supplier?.name}</div>
                        </td>
                        <td className="payment-col--optional">
                            <div>{payment.description}</div>
                        </td>
                        <td className="payment-col--optional">
                            <div>{formatDateToString(new Date(payment.date))}</div>
                        </td>
                        <td className="payment-col--essential">
                            <div>{formatDateToString(parseLocalDate(payment.dueDate))}</div>
                        </td>
                        <td className="payment-col--optional">
                            <div>{payment.paymentDate ? formatDateToString(parseLocalDate(payment.paymentDate)) : 'Pendente'}</div>
                        </td>
                        <td className="payment-col--essential">
                            <div>R$ {payment.value?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}</div>
                        </td>
                        <td className="payment-col--actions table-actions">
                            <TableRowActions actions={buildPaymentRowActions(payment)} />
                        </td>
                    </tr>
                ))}
                </tbody>
                {payments.length === 0 && (<tfoot><tr><td colSpan={8}>{loading ? 'Carregando...' : 'Nenhum item encontrado.'}</td></tr></tfoot>)}
            </table>
            </div>
        </ListPageLayout>
    );
};

export default PaymentList;
